<template>
  <div id="app" :class="appClasses">
    <ErrorPage v-if="error" v-bind="{error}" />
    <Router v-else v-bind="{height, width, isMobile}" />
  </div>
</template>

<script>
import EventListenerMixin from './EventListenerMixin.vue'
import ErrorPage from './ErrorPage.vue'
import Router from './Router.vue'

function getHeight() {
  return document.documentElement.clientHeight
}

function getWidth() {
  return document.documentElement.clientWidth
}

function getSettings() {
  let backToSearch = new URLSearchParams(window.location.search).get(
    'backToSearch',
  )

  // backToSearch provided by search app was broken until post v5:
  // it had two colons after the protocol.
  // https://github.com/Tizra/search/commit/a76c465bda63036394bc179c84269a5a0c5364e3
  if (backToSearch) {
    backToSearch = backToSearch.replace(/^(https?:):/, '$1')
  }

  return {
    advancedSearchLink: '~advancedSearch', // relative to book, blank to omit
    attachmentsIcon: 'paperclip',
    backToSearch,
    feedbackSurvey: false,
    homeIcon: 'home',
    homeLink: '/', // blank to omit
    detailLink: null, // null for pubInfo, blank to omit
    ...(window.tizra && window.tizra.reader),
  }
}

export default {
  name: 'App',

  mixins: [EventListenerMixin],

  data() {
    return {
      error: null,
      height: getHeight(),
      width: getWidth(),
      seenTouch: false,
      settings: getSettings(),
    }
  },

  computed: {
    appClasses() {
      const responsiveClasses = [
        this.height > 332 ? 'hgt332' : 'hlt333',
        this.width > 332 ? 'gt332' : 'lt333',
        this.width > 412 ? 'gt412' : 'lt413',
        this.width > 736 ? 'gt736' : 'lt737',
        this.seenTouch ? '' : 'has-hover',
      ]

      const toolClasses = []
      if (navigator.share) {
        toolClasses.push('t-view-share-button')
      }

      return [...responsiveClasses, ...toolClasses]
    },

    isMobile() {
      // For the mobile MVP, be liberal detecting mobile devices
      // since the server is already delivering according to user
      // agent. Anything 1366px (ipad pro landscape) or less with
      // touch support is mobile.
      const isTouch = 'ontouchstart' in window || navigator.msMaxTouchPoints > 0
      return isTouch && this.width <= 1366
    },
  },

  created() {
    // If the browser size/orientation changes, update in props which
    // are passed to Reader.
    const setSizes = () => {
      this.height = getHeight()
      this.width = getWidth()
    }
    // N.B. resize fires for zoom on Chrome but not Firefox.
    this.$addEventListener(window, 'orientationchange resize scroll', setSizes)
    setSizes()

    // If a touch event is seen, disable hovers.
    const touchWatcher = () => {
      this.seenTouch = true
      document.removeEventListener('touchstart', touchWatcher)
    }
    this.$addEventListener(document, 'touchstart', touchWatcher)

    // Allow components to publish fatal errors.
    this.$sub('fatal', e => {
      this.error = e
    })
  },

  provide() {
    return {
      settings: this.settings,
    }
  },

  components: {ErrorPage, Router},
}
</script>
